import type { SegmentsBoolExp, SyncsBoolExp } from "src/graphql";
import type { FolderType } from "src/components/folders/types";
import type { SelectedFolder } from "src/components/folders/use-folder-state";
import { QueryType } from "src/types/models";
import { ActiveSyncStatuses, SyncHealthStatus } from "src/utils/syncs";
import { SyncProgressFilter } from "src/components/folders/filters";
import { getLabelFilterKeyAndValue } from "src/components/folders/filter-helpers";
import { simplifyHasuraFilters } from "src/utils/hasura";

export interface SyncFilterParams {
  destinationIds?: string[];
  createdByIds?: string[];
  labels?: string[];
  syncStatuses?: string[];
  lastRunStatuses?: string[];
  alertStatuses?: string[];
  progressStatuses?: string[];
  matchboostingType?: string;
  syncIds?: string[];
}

export type FolderFilterParams =
  // The "No folder" pseudo-folder: segments with no folder, regardless of type.
  | { filterType: "no_folder" }
  | {
      filterType: "folder_filter";
      folderIds: string[];
      folderType: FolderType;
    };

export interface SegmentFilterParams {
  sourceIds?: string[];
  journeyNames?: string[];
  priorityListIds?: string[];
  folderParams?: FolderFilterParams;
}

const isActiveFilter = {
  is_effectively_disabled: { _eq: false },
  last_run_at: { _is_null: false },
};

function buildDestinationFilter(ids: string[] | undefined): SyncsBoolExp {
  if (!ids?.length) return {};
  return { destination_id: { _in: ids } };
}

function buildSyncStatusFilter(statuses: string[] | undefined): SyncsBoolExp {
  if (!statuses?.length) return {};
  return {
    _or: [
      ...(statuses.includes("active") ? [isActiveFilter] : []),
      ...(statuses.includes("disabled")
        ? [
            {
              is_effectively_disabled: { _eq: true },
              last_run_at: { _is_null: false },
            },
          ]
        : []),
      // Includes disabled syncs, so deselecting it hides every sync that has no runs.
      ...(statuses.includes("never-run")
        ? [{ last_run_at: { _is_null: true } }]
        : []),
    ],
  };
}

function buildLastRunStatusFilter(
  statuses: string[] | undefined,
): SyncsBoolExp {
  if (!statuses?.length) return {};
  return { status: { _in: statuses } };
}

function buildAlertsFilter(statuses: string[] | undefined): SyncsBoolExp {
  if (!statuses?.length) return {};
  return {
    ...isActiveFilter,
    _or: statuses
      .filter((s): s is SyncHealthStatus =>
        Object.values(SyncHealthStatus).includes(s as SyncHealthStatus),
      )
      .map((s) => ({ health: { _eq: s } })),
  };
}

function buildProgressFilter(statuses: string[] | undefined): SyncsBoolExp {
  if (!statuses?.length) return {};
  const valid = statuses.filter(
    (s): s is SyncProgressFilter =>
      s === SyncProgressFilter.Running || s === SyncProgressFilter.NotRunning,
  );
  if (!valid.length) return {};
  return {
    _or: [
      ...(valid.includes(SyncProgressFilter.Running)
        ? [{ status: { _in: [...ActiveSyncStatuses] } }]
        : []),
      ...(valid.includes(SyncProgressFilter.NotRunning)
        ? [{ _not: { status: { _in: [...ActiveSyncStatuses] } } }]
        : []),
    ],
  };
}

function buildMatchboostingFilter(type: string | undefined): SyncsBoolExp {
  if (!type) return {};
  switch (type) {
    case "boosted_models_syncs":
      return {
        _or: [
          {
            _and: [
              { segment: { matchboosting_enabled: { _eq: true } } },
              {
                config: {
                  _cast: { String: { _like: `%"type": "boosted"%` } },
                },
              },
            ],
          },
          {
            _and: [
              {
                segment: {
                  parent: { matchboosting_enabled: { _eq: true } },
                },
              },
              {
                config: {
                  _cast: { String: { _like: `%"type": "boosted"%` } },
                },
              },
            ],
          },
        ],
      };
    case "boosted_models":
      return { segment: { matchboosting_enabled: { _eq: true } } };
    default:
      return {};
  }
}

function buildLabelFilter(labels: string[] | undefined): SyncsBoolExp {
  if (!labels?.length) return {};
  return {
    _or: labels.map((id) => {
      if (id === "-1") {
        return { tags: { _eq: {} } };
      }
      const { key, value } = getLabelFilterKeyAndValue(id);
      return { tags: { _contains: { [key]: value } } };
    }),
  };
}

function buildCreatedFilter(ids: string[] | undefined): SyncsBoolExp {
  if (!ids?.length) return {};
  return { created_by: { _in: ids } };
}

function buildSourceFilter(ids: string[] | undefined): SegmentsBoolExp {
  if (!ids?.length) return {};
  return { connection_id: { _in: ids } };
}

function buildJourneyFilter(names: string[] | undefined): SegmentsBoolExp {
  if (!names?.length) return {};
  return {
    query_type: { _eq: QueryType.JourneyNode },
    _or: names.map((name) => ({ name: { _ilike: `%${name}%` } })),
  };
}

function buildPriorityListFilter(ids: string[] | undefined): SegmentsBoolExp {
  if (!ids?.length) return {};
  return { priority_list_memberships: { priority_list_id: { _in: ids } } };
}

function buildFolderFilter(
  params: FolderFilterParams | undefined,
): SegmentsBoolExp {
  if (params === undefined) {
    return {};
  }

  if (params.filterType === "no_folder") {
    return { folder_id: { _is_null: true } };
  }

  const folderParams = params.folderIds.length
    ? { folder_id: { _in: params.folderIds } }
    : {};

  switch (params.folderType) {
    case "models":
      return {
        query_type: { _nin: [QueryType.Visual, QueryType.JourneyNode] },
        is_schema: { _eq: false },
        ...folderParams,
      };
    case "journeys":
      return {
        query_type: { _eq: QueryType.JourneyNode },
        ...folderParams,
      };
    case "decisioning":
      return {
        _or: [{ flow_message: {} }, { channel_syncs: {} }],
        ...folderParams,
      };
    case "audiences":
      return {
        query_type: { _eq: QueryType.Visual },
        ...folderParams,
      };
    default:
      return {};
  }
}

/** Builds a bool_exp for the `syncs` table. */
export function buildSyncHasuraFilters(params: SyncFilterParams): SyncsBoolExp {
  return simplifyHasuraFilters({
    _and: [
      buildDestinationFilter(params.destinationIds),
      buildSyncStatusFilter(params.syncStatuses),
      buildLastRunStatusFilter(params.lastRunStatuses),
      buildAlertsFilter(params.alertStatuses),
      buildMatchboostingFilter(params.matchboostingType),
      buildProgressFilter(params.progressStatuses),
      buildLabelFilter(params.labels),
      buildCreatedFilter(params.createdByIds),
      ...(params.syncIds?.length ? [{ id: { _in: params.syncIds } }] : []),
    ],
  });
}

/** Builds a bool_exp for the `segments` table (source, journey, priority list, and folder filters all target segments). */
export function buildSegmentHasuraFilters(
  params: SegmentFilterParams,
): SegmentsBoolExp {
  return simplifyHasuraFilters({
    is_general_model: { _eq: false },
    _and: [
      buildSourceFilter(params.sourceIds),
      buildJourneyFilter(params.journeyNames),
      buildPriorityListFilter(params.priorityListIds),
      buildFolderFilter(params.folderParams),
    ],
  });
}

export function buildFolderFilterParams(
  selectedFolder: SelectedFolder | null,
  selectedFolderType: FolderType | undefined,
): FolderFilterParams | undefined {
  if (selectedFolder?.noFolder) {
    return { filterType: "no_folder" };
  }

  if (!selectedFolderType) {
    return undefined;
  }

  const folderIds = selectedFolder
    ? [selectedFolder.id, ...selectedFolder.flattenedChildren.map((f) => f.id)]
    : [];

  return {
    filterType: "folder_filter",
    folderIds,
    folderType: selectedFolderType,
  };
}

/**
 * Parse URL search params into filter params.
 * Only reads params that can be resolved without server data
 * (i.e. no views, no folders).
 */
export function parseSyncFilterParams(
  searchParams: URLSearchParams,
): SyncFilterParams & SegmentFilterParams {
  const split = (key: string) => {
    const value = searchParams.get(key);
    return value ? value.split(",") : undefined;
  };

  return {
    destinationIds: split("destination"),
    sourceIds: split("source"),
    createdByIds: split("created"),
    labels: split("label"),
    syncStatuses: split("syncStatus"),
    lastRunStatuses: split("status"),
    alertStatuses: split("alerts"),
    progressStatuses: split("progress"),
    matchboostingType: searchParams.get("matchboosting") ?? undefined,
    journeyNames: split("journey"),
    priorityListIds: split("priority_list"),
    syncIds: split("id"),
  };
}
